import type { StaffingOperationsPort } from '../operations/port';
import { staffingPlanInput, type StaffingPlan, type StaffingPlanInput } from '../operations/planModel';
import { getFixtureStaffingPlan, saveFixtureStaffingPlan } from '../fixtures/staffingPlanFixtures';
import { errorStatus } from './staffingPagePresentation';

type DraftPort = Pick<StaffingOperationsPort, 'createPlan' | 'updatePlan'>;
type SaveAttempt = { input: StaffingPlanInput; fingerprint: string; key: string; id?: string; version?: number };

export class StaffingPlanDraftConflict extends Error {
  readonly status = 409;
  constructor() { super('다른 곳에서 변경한 초안입니다. 최신 발령안을 다시 열어 주세요.'); }
}

export function staffingPlanFingerprint(input: StaffingPlanInput) {
  return JSON.stringify(staffingPlanInput(input));
}

/** 한 편집 세션의 순차 저장과 응답 유실 재시도를 소유합니다. */
export class StaffingPlanDraftController {
  private plan?: StaffingPlan;
  private fingerprint?: string;
  private queue: Promise<void> = Promise.resolve();
  private uncertain?: SaveAttempt;
  private conflict = false;

  constructor(private readonly options: {
    initial?: StaffingPlan; port: DraftPort; fixtureMode?: boolean;
    createId?: () => string; onPersisted?: (plan: StaffingPlan) => void;
  }) {
    this.plan = options.initial;
    this.fingerprint = options.initial ? staffingPlanFingerprint(options.initial) : undefined;
  }

  get saved() { return this.plan; }
  get isConflict() { return this.conflict; }
  get hasUncertainSave() { return Boolean(this.uncertain); }
  isSaved(input: StaffingPlanInput) { return !this.uncertain && this.fingerprint === staffingPlanFingerprint(input); }

  persist(input: StaffingPlanInput, retry = false): Promise<StaffingPlan> {
    const snapshot = structuredClone(staffingPlanInput(input));
    const result = this.queue.then(async () => {
      if (this.conflict) throw new StaffingPlanDraftConflict();
      if (this.uncertain) {
        if (!retry) throw new Error('이전 저장 결과를 확인하지 못했습니다. 저장을 다시 시도해 주세요.');
        // 새 입력을 보내기 전에 원래 요청의 같은 key·version으로 저장 결과를 복구합니다.
        await this.execute(this.uncertain);
      }
      const fingerprint = staffingPlanFingerprint(snapshot);
      if (this.plan && this.fingerprint === fingerprint) return this.plan;
      const attempt = { input: snapshot, fingerprint, id: this.plan?.id, version: this.plan?.version, key: this.options.createId?.() ?? crypto.randomUUID() };
      return this.execute(attempt);
    });
    this.queue = result.then(() => undefined, () => undefined);
    return result;
  }

  private async execute(attempt: SaveAttempt): Promise<StaffingPlan> {
    try {
      let saved: StaffingPlan;
      if (this.options.fixtureMode) {
        const current = attempt.id ? getFixtureStaffingPlan(attempt.id) : undefined;
        if (attempt.id && (!current || current.version !== attempt.version || current.status !== 'DRAFT')) throw new StaffingPlanDraftConflict();
        const now = new Date().toISOString();
        saved = { ...attempt.input, id: attempt.id ?? `fixture-plan-${attempt.key}`, version: (attempt.version ?? 0) + 1, status: 'DRAFT', createdAt: current?.createdAt ?? now, updatedAt: now };
        saveFixtureStaffingPlan(saved);
      } else {
        saved = attempt.id
          ? await this.options.port.updatePlan(attempt.id, attempt.input, attempt.version!, attempt.key)
          : await this.options.port.createPlan(attempt.input, attempt.key);
      }
      if (!saved.id || saved.status !== 'DRAFT' || saved.version !== (attempt.version ?? 0) + 1 || (attempt.id && saved.id !== attempt.id)) throw new StaffingPlanDraftConflict();
      this.plan = saved;
      this.fingerprint = attempt.fingerprint;
      this.uncertain = undefined;
      this.options.onPersisted?.(saved);
      return saved;
    } catch (error) {
      const status = errorStatus(error);
      if (status === 409) { this.conflict = true; this.uncertain = undefined; }
      else if (!status || status >= 500) this.uncertain = attempt;
      else this.uncertain = undefined;
      throw error;
    }
  }
}
